import axios from "axios";
import { RequestDto } from "./dtos/request/RequestDto";
import { ResponseDto } from "./dtos/response/ResponseDto";

class BaseService {
  private domainEndpoint: string = process.env.REACT_APP_BASE_URL ?? "";

  private getUrlEndpoint(resource: string) {
    return this.domainEndpoint + resource;
  }

  protected async doPost<T>(
    dto: RequestDto,
    postRecover: (responseDto: T) => void,
    errorRecover: (errorMessage: string) => void
  ): Promise<void> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...dto.headers,
      };
      
      const url = this.getUrlEndpoint(dto.resource);
      console.log("[BaseService] POST request:", { url, data: dto.data });
      
      const { data, status } = await axios.post<T>(
        url,
        dto.data,
        {
          headers: headers,
          validateStatus: (status) => status < 500,
        }
      );
      
      // Manejar errores HTTP (404, 403, etc.)
      if (status >= 400) {
        const errorData = data as any;
        const errorMessage = errorData?.ErrorMessage || 
                            (status === 404 ? `Endpoint no encontrado: ${url}` : 
                             `Error HTTP ${status}: ${errorData?.message || 'Error desconocido'}`);
        console.error("[BaseService] Error HTTP:", { status, url, data });
        errorRecover(errorMessage);
        return;
      }
      
      const response = this.GetResponseData<T>(data, status);

      if (response.ErrorCode !== 0) {
        errorRecover(response.ErrorMessage || "Hubo un error inesperado.");
      } else {
        postRecover(response.Data as T);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.ErrorMessage || 
                          error.message || 
                          "Hubo un error inesperado.";
      console.error("[BaseService] Error en POST:", {
        url: this.getUrlEndpoint(dto.resource),
        error: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
      errorRecover(errorMessage);
      throw error;
    }
  }

  protected async doGet<T>(
    dto: RequestDto,
    postRecover: (responseDto: T) => void,
    errorRecover: (errorMessage: string) => void
  ): Promise<void> {
    try {
      const url = this.getUrlEndpoint(dto.resource);
      const { data, status } = await axios.get<T>(
        url,
        { params: dto.data, validateStatus: (status) => status < 500 }
      );
      
      // Manejar errores HTTP (400, 404, 403, etc.)
      if (status >= 400) {
        const errorData = data as any;
        const errorMessage = errorData?.ErrorMessage || 
                            errorData?.message ||
                            (status === 404 ? `Endpoint no encontrado: ${url}` : 
                             `Error HTTP ${status}: Error desconocido`);
        console.error("[BaseService] Error HTTP:", { status, url, data });
        errorRecover(errorMessage);
        return;
      }
      
      const response = this.GetResponseData<T>(data, status);

      if (response.ErrorCode !== 0) {
        errorRecover(response.ErrorMessage || "Hubo un error inesperado.");
      } else {
        postRecover(response.Data as T);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.ErrorMessage || 
                          error.response?.data?.message ||
                          error.message || 
                          "Hubo un error inesperado, inténtelo más tarde.";
      console.error("[BaseService] Error en GET:", {
        url: this.getUrlEndpoint(dto.resource),
        error: errorMessage,
        status: error.response?.status,
        data: error.response?.data
      });
      errorRecover(errorMessage);
    }
  }

  private GetResponseData<T>(data: any, status: number): ResponseDto<T> {
    const response = new ResponseDto<T>();

    response.code = status;
    response.ErrorCode = data.ErrorCode;
    response.ErrorMessage = data.ErrorMessage;
    response.Data = data.Data;

    return response;
  }
}

export default BaseService;
